import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import API_URL from '../config/api';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Get plan from URL query params
  const queryParams = new URLSearchParams(location.search);
  const plan = queryParams.get('plan') || 'business';

  const plans = {
    starter: {
      name: 'Starter',
      price: '$29',
      priceId: 'price_1Sqfvn3lL4UD6sHd4zSJsF14',
      features: [
        'Up to 3 devices',
        'Basic threat detection',
        'Weekly reports',
        'Email support',
        '14-day free trial'
      ]
    },
    business: {
      name: 'Business',
      price: '$49',
      priceId: 'price_1Sqfy83lL4UD6sHd8WBZ0sPB',
      features: [
        'Up to 10 users',
        'Advanced threat detection',
        'Real-time alerts',
        'Two-factor authentication',
        'Priority support',
        'Compliance reports',
        '14-day free trial'
      ]
    },
    enterprise: {
      name: 'Enterprise',
      price: '$149',
      priceId: 'price_1Sqfz13lL4UD6sHdUejsKkjv',
      features: [
        'Unlimited users',
        'Custom integrations',
        'Dedicated account manager',
        'API access',
        'White-label option',
        '24/7 phone support',
        '14-day free trial'
      ]
    }
  };

  const selectedPlan = plans[plan] || plans.business;

  const handleCheckout = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');

      // Create checkout session
      const response = await fetch(`${API_URL}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          priceId: selectedPlan.priceId,
          planName: selectedPlan.name,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Modern way: Just redirect to Stripe's URL
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to process checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Complete Your Purchase</h1>
          <p className="text-gray-600">Start your 14-day free trial today</p>
        </div>

        {/* Plan Details Card */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <div className="border-b pb-6 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedPlan.name} Plan</h2>
                <p className="text-gray-600 mt-1">Perfect for your business needs</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-blue-600">{selectedPlan.price}</div>
                <div className="text-gray-600">/month</div>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">What's Included:</h3>
            <ul className="space-y-3">
              {selectedPlan.features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Trial Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <svg className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="font-semibold text-blue-900 mb-1">14-Day Free Trial</h4>
                <p className="text-sm text-blue-800">
                  You won't be charged for 14 days. Cancel anytime during your trial at no cost.
                </p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Checkout Button */}
          <button
            onClick={handleCheckout}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : (
              'Start Free Trial'
            )}
          </button>

          {/* Security Info */}
          <div className="mt-6 text-center text-sm text-gray-600">
            <p className="flex items-center justify-center">
              <svg className="h-5 w-5 text-green-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Secure payment powered by Stripe
            </p>
          </div>
        </div>

        {/* Change Plan */}
        <div className="text-center">
          <button
            onClick={() => navigate('/pricing')}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Change Plan
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;